/**
 * BillingMetricsPage
 * Métricas de faturamento (MRR, ARR, assinaturas ativas, churn, LTV)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import { TrendingUp, People, AttachMoney, Assessment } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductScopeGate } from '@/components/ProductScopeGate';
import { parseLoadError } from '@/utils/productScope';

interface BillingMetrics {
  mrr: number;
  arr: number;
  activeSubscriptions: number;
  churnRate: number;
  ltv: number;
  period: { from: string; to: string };
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`;

export const BillingMetricsPage: React.FC = () => {
  const { setRefreshCallback, isRefreshing } = useRefresh();
  const { productId, selectedProductIds } = useFilters();
  const productsById = useProductsMap();
  const scopeProductIds = useMemo(() => {
    if (selectedProductIds.length > 0) return selectedProductIds;
    const all = Array.from(productsById.keys());
    return all.length > 0 ? all : [productId];
  }, [selectedProductIds, productsById, productId]);
  const today = new Date();
  const [from, setFrom] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [metrics, setMetrics] = useState<BillingMetrics | null>(null);
  const [metricsByProduct, setMetricsByProduct] = useState<Record<string, BillingMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [partialWarnings, setPartialWarnings] = useState<string[]>([]);

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setPartialWarnings([]);
      const productIds = scopeProductIds.length ? scopeProductIds : [productId].filter(Boolean);
      if (!productIds.length) {
        setMetrics(null);
        setMetricsByProduct({});
        setLoading(false);
        return;
      }
      const responses = await Promise.all(
        productIds.map(async pid => {
          try {
            const res = await apiService.getBillingMetrics(from, to, pid);
            return { productId: pid, ok: true as const, data: res.data as BillingMetrics };
          } catch (err: unknown) {
            const parsed = parseLoadError(err);
            return { productId: pid, ok: false as const, error: parsed };
          }
        })
      );
      const warnings = responses
        .filter(
          (r): r is { productId: string; ok: false; error: ReturnType<typeof parseLoadError> } =>
            !r.ok
        )
        .map(
          r =>
            `${productsById.get(r.productId)?.displayName || r.productId}: ${r.error.title} — ${r.error.detail}`
        );
      setPartialWarnings(warnings);
      const okRows = responses.filter(
        (r): r is { productId: string; ok: true; data: BillingMetrics } => r.ok
      );
      if (!okRows.length) {
        setLoadError(
          Object.assign(new Error(warnings[0] || 'Nenhum produto retornou métricas'), {
            response: { status: 503, data: { detail: warnings.join('\n') } }
          })
        );
        setMetrics(null);
        setMetricsByProduct({});
        return;
      }
      const byProduct = Object.fromEntries(okRows.map(r => [r.productId, r.data])) as Record<
        string,
        BillingMetrics
      >;
      setMetricsByProduct(byProduct);
      const values = okRows.map(r => r.data);
      setMetrics({
        mrr: values.reduce((sum, item) => sum + (item.mrr || 0), 0),
        arr: values.reduce((sum, item) => sum + (item.arr || 0), 0),
        activeSubscriptions: values.reduce((sum, item) => sum + (item.activeSubscriptions || 0), 0),
        churnRate: values.length
          ? values.reduce((sum, item) => sum + (item.churnRate || 0), 0) / values.length
          : 0,
        ltv: values.length
          ? values.reduce((sum, item) => sum + (item.ltv || 0), 0) / values.length
          : 0,
        period: values[0]?.period || { from, to }
      });
    } catch (err: unknown) {
      setLoadError(err);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, scopeProductIds, productId, productsById]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    setRefreshCallback(loadMetrics);
    return () => setRefreshCallback(null);
  }, [loadMetrics, setRefreshCallback]);

  const singleScopeId =
    scopeProductIds.length === 1 ? scopeProductIds[0] : productId?.trim() || undefined;

  return (
    <ProductScopeGate
      productId={singleScopeId}
      requireSelection={false}
      integration={singleScopeId ? 'billing' : 'core'}
      loading={loading}
      loadError={loadError}
      isEmpty={
        !loading &&
        !loadError &&
        !!metrics &&
        metrics.mrr === 0 &&
        metrics.arr === 0 &&
        metrics.activeSubscriptions === 0
      }
      emptyTitle="Sem métricas no período"
      emptyDetail="O billing respondeu corretamente, mas não há assinaturas ou receita recorrente no intervalo selecionado."
      onRetry={loadMetrics}
    >
      <Box sx={{ p: 3 }}>
        {partialWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Alguns produtos não carregaram métricas (os demais foram agregados):
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {partialWarnings.map(w => (
                <li key={w}>
                  <Typography variant="body2">{w}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Métricas de Billing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Indicadores calculados a partir do vover-billing para o escopo de produto
                  selecionado. Use esta página para entender receita recorrente, assinaturas ativas
                  e saúde da base no período.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {scopeProductIds.map(productId => (
                  <ProductCell
                    key={productId}
                    productId={productId}
                    name={productsById.get(productId)?.displayName}
                    avatarUrl={productsById.get(productId)?.avatarUrl}
                  />
                ))}
                <Chip
                  size="small"
                  color={scopeProductIds.length > 1 ? 'primary' : 'default'}
                  label={scopeProductIds.length > 1 ? 'Visão agregada' : 'Visão individual'}
                />
                <Chip size="small" variant="outlined" label="Fonte: vover-billing" />
                <Chip
                  size="small"
                  variant="outlined"
                  label="Atualização sob demanda ao abrir/atualizar"
                />
              </Stack>
              <Alert severity="info">
                Estes valores são consultas agregadas no billing para o período selecionado. Não
                exigem ação manual de coleta; a tela recalcula ao carregar, alterar datas ou clicar
                em Atualizar. Não confundir com o <strong>MRR estimado</strong> do Fluxo financeiro,
                que vem da configuração de precificação.
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Período de análise
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                label="De"
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item>
              <TextField
                label="Até"
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={loadMetrics}
                disabled={loading || isRefreshing}
                startIcon={loading || isRefreshing ? <CircularProgress size={20} /> : null}
              >
                Atualizar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading && !metrics ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : metrics ? (
          <>
            {metrics.mrr === 0 && metrics.arr === 0 && metrics.activeSubscriptions === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Sem faturas pagas ou assinaturas ativas no período selecionado para este produto.
                Ajuste as datas ou confirme se o produto possui billing habilitado.
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AttachMoney color="primary" />
                      <Typography variant="overline">MRR</Typography>
                    </Box>
                    <Typography variant="h5">{formatCurrency(metrics.mrr)}</Typography>
                    <Typography variant="caption">
                      Receita recorrente mensal calculada no período filtrado.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUp color="primary" />
                      <Typography variant="overline">ARR</Typography>
                    </Box>
                    <Typography variant="h5">{formatCurrency(metrics.arr)}</Typography>
                    <Typography variant="caption">Receita Recorrente Anual</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <People color="primary" />
                      <Typography variant="overline">Assinaturas Ativas</Typography>
                    </Box>
                    <Typography variant="h5">{metrics.activeSubscriptions}</Typography>
                    <Typography variant="caption">Clientes com pagamento confirmado</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Assessment color="primary" />
                      <Typography variant="overline">Churn Rate</Typography>
                    </Box>
                    <Typography variant="h5">{formatPercent(metrics.churnRate)}</Typography>
                    <Typography variant="caption">
                      Taxa de cancelamento. Validar base antes de decisões executivas.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AttachMoney color="primary" />
                      <Typography variant="overline">LTV</Typography>
                    </Box>
                    <Typography variant="h5">{formatCurrency(metrics.ltv)}</Typography>
                    <Typography variant="caption">
                      Lifetime Value médio. Indicador experimental quando há pouca base histórica.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {scopeProductIds.length > 1 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        Detalhe por produto
                      </Typography>
                      <Grid container spacing={2}>
                        {scopeProductIds.map(productId => {
                          const item = metricsByProduct[productId];
                          return (
                            <Grid item xs={12} md={6} lg={4} key={productId}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Stack spacing={1}>
                                    <ProductCell
                                      productId={productId}
                                      name={productsById.get(productId)?.displayName}
                                      avatarUrl={productsById.get(productId)?.avatarUrl}
                                    />
                                    <Typography variant="body2">
                                      MRR: <strong>{formatCurrency(item?.mrr || 0)}</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Assinaturas ativas: {item?.activeSubscriptions || 0}
                                    </Typography>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </>
        ) : null}
      </Box>
    </ProductScopeGate>
  );
};
